import { logger } from '@sentry/utils'
import {
  Button,
  Form,
  Input,
  Modal,
  ModalProps,
  Select,
  notification,
} from 'antd'
import { FunctionComponent, useEffect } from 'react'
import { nameof } from 'ts-simple-nameof'
import {
  useAddKolContactInfoMutation,
  useEditKolContactInfoMutation,
} from '@/api/workspace-api/directory-api'
import { ModalTitle } from '@/components/common/modal-title'
import { useKolContactInfo } from '@/components/kol/common/kol-cooperation-detail-drawer/contact-info/use-kol-contact-info'
import { IrmModalWrapper } from '@/components/kol/irm/common/irm-modal-wrapper'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  ContactInfo,
  ContactType,
  UpdateContactInfo,
  contactTypeI18nMap,
  contactTypeSchema,
} from '@/types/schema/directory/kol-management'
import { toVoidFunction } from '@/utils/to-void-function'

const { Option } = Select
const { Item } = Form

interface ContactInfoModalProps extends Pick<ModalProps, 'open'> {
  kolId: string
  contact?: ContactInfo
  onClose: VoidFunction
}

interface ContactInfoForm {
  type: ContactType
  value?: string
  notes?: string
}

const ContactInfoModal: FunctionComponent<ContactInfoModalProps> = ({
  open,
  onClose,
  kolId,
  contact,
}) => {
  const { formatMessage } = useIntl()
  const [addContactInfo, { isLoading: isAddContactInfoLoading }] =
    useAddKolContactInfoMutation()
  const [editContactInfo, { isLoading: isEditContactInfoLoading }] =
    useEditKolContactInfoMutation()
  const { handleEditContactEvent } = useKolContactInfo(kolId)

  const [form] = Form.useForm<ContactInfoForm>()
  const contactType = Form.useWatch<ContactInfoForm['type']>(
    nameof<ContactInfoForm>((n) => n.type),
    form,
  )

  const isCreate = !contact || contact.directory_contact_id < 0 // mock empty data, id < 0
  const isCustomCreated = contact && contact?.directory_contact_id > 0
  const disableEdit = !isCreate && !isCustomCreated

  const handleChangeContactInfo = async ({
    type,
    value = '',
    notes,
  }: ContactInfoForm): Promise<void> => {
    const contactInfo: UpdateContactInfo = {
      type,
      value,
      ...(type === 'email'
        ? {
            notes,
          }
        : {}),
    }

    try {
      if (isCreate) {
        await addContactInfo({
          kolId,
          contactInfo,
        }).unwrap()
        handleClose()
      } else {
        await editContactInfo({
          kolId,
          contactInfoId: contact.directory_contact_id,
          contactInfo,
        }).unwrap()
        handleClose()
      }
      handleEditContactEvent(type, value === '')
    } catch (e) {
      logger.error(e)
      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
        duration: 1,
      })
    }
  }

  const handleClose = (): void => {
    onClose()
    form.resetFields()
  }

  const handleSubmit = async (values: ContactInfoForm): Promise<void> => {
    await handleChangeContactInfo(values)
  }

  useEffect(() => {
    if (!open) {
      return
    }

    form.setFieldsValue({
      notes: contact?.notes ?? '',
      value: contact?.value ?? '',
      type: !contact?.type ? 'email' : contact.type,
    })
  }, [contact, form, open])

  return (
    <Modal
      centered
      destroyOnClose
      footer={null}
      open={open}
      onCancel={handleClose}
    >
      <Form colon={false} form={form} onFinish={toVoidFunction(handleSubmit)}>
        <IrmModalWrapper>
          <ModalTitle>
            {formatMessage({
              id: isCreate
                ? 'irm:update_title_added_contact_info'
                : 'irm:update_contact_info',
            })}
          </ModalTitle>
          <Item
            name='value'
            rules={
              contactType === 'email'
                ? [
                    {
                      type: 'email',
                      message: formatMessage({ id: 'error:email_fail' }),
                    },
                  ]
                : []
            }
          >
            <Input
              addonBefore={
                <Item noStyle name='type'>
                  <Select
                    disabled={!isCreate}
                    size='large'
                    style={{ width: '124px' }}
                  >
                    {contactTypeSchema.options.map((type) => (
                      <Option key={type} value={type}>
                        {formatMessage({ id: contactTypeI18nMap[type] })}
                      </Option>
                    ))}
                  </Select>
                </Item>
              }
              disabled={disableEdit}
              size='large'
              style={{ width: '100%' }}
            />
          </Item>

          {contactType === 'email' && (
            <Item name='notes'>
              <Input
                disabled={disableEdit}
                maxLength={20}
                placeholder={formatMessage({ id: 'irm:email_remark' })}
                size='large'
              />
            </Item>
          )}

          <Button
            htmlType='submit'
            loading={isAddContactInfoLoading || isEditContactInfoLoading}
            type='primary'
          >
            {formatMessage({ id: 'general:submit_btn' })}
          </Button>
        </IrmModalWrapper>
      </Form>
    </Modal>
  )
}

export default ContactInfoModal
